'use client'

import { useEffect } from 'react'

import type { AmplitudeConfig } from './schema'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

/** Paths excluded from Amplitude autocapture page views (matches ui-react) */
const EXCLUDED_PAGE_VIEW_PATTERNS = ['/depart', '/return', '/checkout']

/**
 * Initializes the Amplitude SDK with autocapture toggles driven by config.
 *
 * Both nexus and ui-react share the same API key, so the SDK natively shares
 * the device ID cookie — no custom bridging code needed.
 *
 * Autocapture categories (`elementInteractions`, `fileDownloads`,
 * `formInteractions`) are off in prod because Amplitude filters those
 * categories server-side. `pageViews` + `sessions` stay on everywhere.
 *
 * Flow:
 * 1. Add four enrichment plugins so they enrich all events including the
 *    initial page view: source, snowplow-ids, device-form-factor, page-type.
 *    The plugins set disjoint property keys, so attach order does not affect
 *    the emitted payload.
 * 2. Add the session-replay plugin (fail-closed: sampleRate 0). Capture scope
 *    — sampling, kill-switch, URL targeting, masking overrides — is governed by
 *    Amplitude remote config in the UI, not here.
 * 3. Call amplitude.init() with shared key
 * 4. After init resolves: bridge Amplitude device ID to Snowplow global context
 *
 * Dynamic imports `@amplitude/analytics-browser` in useEffect to keep ~36KB off critical path.
 * Renders nothing (returns null).
 */
export function AmplitudeScript({
    config,
    appVersion,
}: {
    config: AmplitudeConfig
    appVersion: string
}) {
    useEffect(() => {
        async function initAmplitude() {
            const [
                amplitude,
                { createSnowplowIdsPlugin },
                { createSourcePlugin },
                { createDeviceFormFactorPlugin },
                { createPageTypePlugin },
                { sessionReplayPlugin },
            ] = await Promise.all([
                import('@amplitude/analytics-browser'),
                import('./snowplow-ids-plugin'),
                import('./source-plugin'),
                import('./device-form-factor-plugin'),
                import('./page-type-plugin'),
                import('@amplitude/plugin-session-replay-browser'),
            ])

            amplitude.add(createSourcePlugin('nexus'))
            amplitude.add(createSnowplowIdsPlugin())
            amplitude.add(createDeviceFormFactorPlugin())
            amplitude.add(createPageTypePlugin())
            // Session replay: fail-closed. sampleRate 0 records nothing until
            // Amplitude remote config (managed in the UI) sets a rate — so
            // sampling, the kill-switch, and URL targeting live there, not here.
            // useWebWorker offloads replay compression off the main thread.
            // privacyConfig pins the masking baseline to 'light' (masks the
            // sensitive-input subset — passwords, payment, phone, email — and
            // leaves other inputs/text visible); element-level overrides are
            // managed in the Amplitude UI and take precedence.
            amplitude.add(
                sessionReplayPlugin({
                    sampleRate: 0,
                    useWebWorker: true,
                    privacyConfig: { defaultMaskLevel: 'light' },
                }),
            )

            void amplitude
                .init(config.apiKey, {
                    serverUrl: config.serverUrl,
                    appVersion,
                    // SDK internal health telemetry posts to diagnostics.*.amplitude.com
                    // every 5 min — useless to us, blocked by the same ad/tracker
                    // protections, and there's no proxy option for it. Turn it off.
                    enableDiagnostics: false,
                    // Secure is off by default in the SDK. `domain` is deliberately not
                    // set: the auto-detected top-level domain is what shares the device
                    // id cookie with ui-react.
                    cookieOptions: { secure: true, sameSite: 'Lax' },
                    // Proxy the session-replay remote-config fetch through our first-party
                    // domain when configured (sr-client-cfg.amplitude.com is ad-blocked).
                    ...(config.remoteConfigServerUrl
                        ? { remoteConfig: { serverUrl: config.remoteConfigServerUrl } }
                        : {}),
                    autocapture: {
                        attribution: config.autocapture.attribution
                            ? { resetSessionOnNewCampaign: true }
                            : false,
                        elementInteractions: config.autocapture.elementInteractions,
                        fileDownloads: config.autocapture.fileDownloads,
                        formInteractions: config.autocapture.formInteractions,
                        pageViews: config.autocapture.pageViews
                            ? {
                                  trackOn: () => {
                                      const path = window.location.pathname
                                      return !EXCLUDED_PAGE_VIEW_PATTERNS.some((pattern) =>
                                          path.includes(pattern),
                                      )
                                  },
                              }
                            : false,
                        sessions: config.autocapture.sessions,
                    },
                })
                .promise.then(() => {
                    // Bridge Amplitude device ID into Snowplow global context
                    const ampDeviceId = amplitude.getDeviceId()
                    if (ampDeviceId && window.snowplow) {
                        window.snowplow('addGlobalContexts', [
                            {
                                schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                                data: { ampClientId: ampDeviceId },
                            },
                        ])
                    }
                })

            // Exit-event delivery: the SDK batches events and sends them async,
            // so a same-tab navigation (outbound content link, search submit via
            // window.location) unloads the page before the queue flushes — the
            // event would sit in storage until the next visit. On pagehide,
            // switch to the sendBeacon transport (delivery survives unload) and
            // flush the queue; if the page is resurrected from the back/forward
            // cache, restore the default fetch transport (beacon has no retries).
            window.addEventListener('pagehide', () => {
                amplitude.setTransport('beacon')
                void amplitude.flush()
            })
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    amplitude.setTransport('fetch')
                }
            })
        }

        void initAmplitude()
    }, [
        config.apiKey,
        config.serverUrl,
        config.remoteConfigServerUrl,
        config.autocapture,
        appVersion,
    ])

    return null
}
